'use strict';
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import DynamicTable from '../components/controls/DynamicTable';
import DynamicTableHelper from '../components/controls/DynamicTableHelper';
import DynamicTableCell from '../components/controls/DynamicTableCell';
import { Icon, Label } from '../components/index';
import { fetchSingleFile, AdminFetchSingleFile } from '../redux/actions/files';
import * as enums from '../enums/Enums';
import { Logger, Log } from '../utils/Logger';
import { AppInsightLogLevel } from '../enums/Enums';
import { Switch, Tooltip, IconButton } from '@mui/material';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { LAB_VANTAGE_EDITED_TOOLTIP } from '@/constants/labVantageUi';
import './FilesTable.scss';
const logger = new Logger();

import { LoadingSpinner } from 'metabux/components/LoadingSpinner/LoadingSpinner';
import { Auditor } from '@/components/util/Auditor';
import { withPermissions } from '@/hooks/permissions.hook';
import * as permissions from '@/security/permissionTypes';
import classNames from 'classnames';
import * as config from '../config/env.config';
import Sort from '../utils/Sort';

export class FilesTable extends Auditor {
	constructor(props) {
		super(props);

		this.isAdmin = props.checkUserHasRole([enums.ROLES.DTS, enums.ROLES.ADMIN]);
        this.state = {
            currentDownloadingFile: null,
            ...super.state
        };
	}

	componentDidUpdate(prevProps) {
		if (this.props != prevProps) {
			this.setState({ files: this.props.files || [] });
		}
	}


	/**Table Support Functions */
	createTableFields = () => {
		let tableFieldsArray = [];

		const actionsField = DynamicTableHelper.createField('actions', { text: 'Actions', className: 'action-cell', style: { textAlign: 'center' } }, { className: 'action-cell', renderer: this.actionsRenderer });
		
		// Only show Hidden column when user has canEditProjects permission and is in admin mode
		const canEditProjects = this.props.checkUserHasPermissions ? this.props.checkUserHasPermissions(permissions.CAN_EDIT_PROJECTS) : false;
		const shouldShowHiddenColumn = (this.isAdmin || canEditProjects) && this.props.useAdminEndpoint;
		const hiddenField = shouldShowHiddenColumn ? 
			DynamicTableHelper.createField('hidden', { text: 'Visible', className: 'hidden-cell', style: { textAlign: 'center' } }, { className: 'hidden-cell', renderer: this.hiddenRenderer }) : 
			null;
		const agentVisibleField = (shouldShowHiddenColumn && config.ENABLE_AGENT_CONTROLS) ? 
			DynamicTableHelper.createField('agentVisible', { text: 'Agent', className: 'agent-visible-cell', style: { textAlign: 'center' } }, { className: 'agent-visible-cell', renderer: this.agentVisibleRenderer }) : 
			null;

		const lineageField = this.props.useAdminEndpoint
			? DynamicTableHelper.createField(
				'lineage',
				{ text: 'Lineage', className: 'lineage-cell' },
				{
					className: 'lineage-cell',
					renderer: this.lineageRenderer,
					comparator: (_sorter, dir) => (itemA, itemB) => {
						const key = (item) => {
							if (item.managed === true) return 'managed';
							if (item.uploadedFromLabVantage === true) return 'lv';
							return '';
						};
						return Sort.compare(key(itemA).toLowerCase(), key(itemB).toLowerCase(), dir);
					},
				},
			)
			: null;

		tableFieldsArray.push(
			DynamicTableHelper.createField('---', {}, { type: 'select' }),
			DynamicTableHelper.createField('type', { text: 'Type', className: 'type-cell' }, { className: 'type-cell', renderer: this.fileTypeRenderer }),
			DynamicTableHelper.createField('name', { text: 'Name', className: 'name-cell' }, { className: 'name-cell', renderer: this.nameRenderer }),
			DynamicTableHelper.createField('customerDate', { text: 'Date', className: 'customerDate' }, { className: 'customerDate', renderer: DynamicTableHelper.defaultDateRenderer }),
			actionsField,
		);

		if (lineageField) {
			tableFieldsArray.push(lineageField);
		}

		// Only add hidden field if it should be shown
		if (hiddenField) {
			tableFieldsArray.push(hiddenField);
		}
		// Only add agent visible field if it should be shown (right after Visible column)
		if (agentVisibleField) {
			tableFieldsArray.push(agentVisibleField);
		}

		return tableFieldsArray;
	}

	fileTypeRenderer(data, fieldName, n) {
		let fileType = FilesTable.getFileTypeIcon(data.name);
		return (
			<DynamicTableCell className="type-cell" key={n}>
				<Label title={fileType} color="primary"><Icon icon={fileType} size="large" color="selectedItem" /></Label>
			</DynamicTableCell>
		);
	}

	lineageRenderer = (data, fieldName, n) => {
		const showManaged = data.managed === true;
		const showLv = data.uploadedFromLabVantage === true;
		return (
			<DynamicTableCell className="lineage-cell" key={n}>
				{(showManaged || showLv) ? (
					<span className="lineage-cell-labels">
						{showManaged && (
							<Label title="This file is managed by mlims v7" color="quaternary">
								Managed by mlims v7
							</Label>
						)}
						{showLv && (
							<Label title="This file was imported using Upload from Lab Vantage" color="quaternary">
								Uploaded from Lab Vantage
							</Label>
						)}
					</span>
				) : (
					<span />
				)}
			</DynamicTableCell>
		);
	};

	nameRenderer = (data, fieldName, n) => {
		const { fileId, name } = data;
		const showEdited =
			this.props.showLabVantageEditedInFilesTable &&
			this.props.labVantageEditedPortalFileIds &&
			this.props.labVantageEditedPortalFileIds.has(String(fileId).trim().toLowerCase());
		const onOpenLabVantage = this.props.onOpenLabVantageUploadModal;

		return (
			<DynamicTableCell className="name-cell" key={n}>
				<span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
					<span title={name}>{name}</span>
					{showEdited && (
						<Tooltip title={LAB_VANTAGE_EDITED_TOOLTIP}>
							<IconButton
								type="button"
								size="small"
								aria-label={`${LAB_VANTAGE_EDITED_TOOLTIP} Open Upload from Lab Vantage.`}
								onClick={(e) => {
									e.stopPropagation();
									if (onOpenLabVantage) onOpenLabVantage(fileId);
								}}
								sx={{ ml: 0.25, p: 0.25 }}
							>
								<WarningAmber fontSize="small" color="warning" />
							</IconButton>
						</Tooltip>
					)}
				</span>
			</DynamicTableCell>
		);
	}

	hiddenRenderer = (data, fieldName, n) => {
		const { customerVisible } = data;

		const handleCustomerVisibilityChange = async (event) => {
			const newCustomerVisible = event.target.checked;
			
			// Create updated file data with all required fields
			const updatedFileData = {
				fileId: data.fileId,
				projectId: data.projectId,
				staged: data.staged,
				name: data.name || '',
				description: data.description || '',
				createdBy: data.createdBy || '',
				managed: data.managed || false,
				customerVisible: newCustomerVisible,
				size: data.size || 0,
				mimeType: data.mimeType || '',
				createdDate: data.createdDate,
				modifiedDate: data.modifiedDate,
				customerDate: data.customerDate
			};

			try {
				await this.props.updateFilePropertiesAdmin(updatedFileData);
				
				// Update the specific file in the visible files to preserve sort order
				if (this.props.updateFileInVisibleFiles) {
					this.props.updateFileInVisibleFiles({ 
						fileId: data.fileId,
						customerVisible: newCustomerVisible 
					});
				}
			} catch (error) {
				console.error('Failed to update file properties:', error);
				// Could show a toast notification here
			}
		};

		return (
			<DynamicTableCell className="hidden-cell" key={n}>
				{this.isAdmin && this.props.useAdminEndpoint && (
					<Tooltip title={customerVisible ? "Hide from customers" : "Show to customers"}>
						<Switch
							checked={customerVisible}
							onChange={handleCustomerVisibilityChange}
							size="small"
							color="primary"
						/>
					</Tooltip>
				)}
			</DynamicTableCell>
		);
	}

	agentVisibleRenderer = (data, fieldName, n) => {
		const { agentVisible = false } = data;

		const handleAgentVisibilityChange = async (event) => {
			const newAgentVisible = event.target.checked;
			
			// Create updated file data with all required fields
			const updatedFileData = {
				fileId: data.fileId,
				projectId: data.projectId,
				staged: data.staged,
				name: data.name || '',
				description: data.description || '',
				createdBy: data.createdBy || '',
				managed: data.managed || false,
				customerVisible: data.customerVisible,
				agentVisible: newAgentVisible,
				size: data.size || 0,
				mimeType: data.mimeType || '',
				createdDate: data.createdDate,
				modifiedDate: data.modifiedDate,
				customerDate: data.customerDate
			};

			try {
				await this.props.updateFilePropertiesAdmin(updatedFileData);
				
				// Update the specific file in the visible files to preserve sort order
				if (this.props.updateFileInVisibleFiles) {
					this.props.updateFileInVisibleFiles({ 
						fileId: data.fileId,
						agentVisible: newAgentVisible 
					});
				}
			} catch (error) {
				console.error('Failed to update file properties:', error);
				// Could show a toast notification here
			}
		};

		return (
			<DynamicTableCell className="agent-visible-cell" key={n}>
				{this.isAdmin && this.props.useAdminEndpoint && (
					<Tooltip title={agentVisible ? "Hide from agents" : "Show to agents"}>
						<Switch
							checked={agentVisible}
							onChange={handleAgentVisibilityChange}
							size="small"
							color="primary"
						/>
					</Tooltip>
				)}
			</DynamicTableCell>
		);
	}

	onRemove = (file) => (e) => {
		e.stopPropagation();
		if (this.props.onRemove) this.props.onRemove(file);
	}

    actionsRenderer = (data, fieldName, n) => {
        const { fileId, name, size, managed } = data;

        let { isLoading, fetchSingleFile } = this.props;
        let { currentDownloadingFile } = this.state;

        const downloadFile = () => {
            let currProjectId = localStorage.getItem("CurrentFilesProjectId");
            this.setState({ currentDownloadingFile: fileId });

            if (this.props.useAdminEndpoint) {
                this.props.AdminFetchSingleFile(fileId, currProjectId, name).then(() => {
                    this.setState({ currentDownloadingFile: "" });
                });
            } else {
                fetchSingleFile(fileId, currProjectId, name).then(() => {
                    this.setState({ currentDownloadingFile: "" });
                });
            }

            let currProjectPublishDate = localStorage.getItem("CurrentProjectPublishDate");
            this.state.logger.logProjectFileDownload(currProjectId, [name]);

            let logDownload = new Log();
            logDownload.SetLevel(AppInsightLogLevel.EVENT);
            logDownload.SetName('PageEvent_FileDownload_Single');
            logDownload.AddProperty('Filesize', size);
            logDownload.AddProperty("PublishDate", currProjectPublishDate);
            logDownload.AddProperty('Count', 1);
            logDownload.AddProperty('ProjectId', currProjectId);
            logger.doLog(logDownload);
        };



        return (
            <DynamicTableCell key={n} className="action-cell">
                <div className="action-container">
                    <div className="action-left">
                        {
                            (isLoading && currentDownloadingFile == fileId)
                                ? <LoadingSpinner />
                                : <Label title={"Download " + name} color="tertiary"><a onClick={downloadFile} >Download <Icon icon="download" /></a></Label>
                        }
                        {this.isAdmin && this.props.onRemove && !managed && (
                                <Label title={"Remove " + name} color="danger"><a onClick={this.onRemove(data)} >Remove <Icon icon="remove" /></a></Label>
                        )}
                    </div>
                </div>
            </DynamicTableCell>
        );
    }


	generateClassNames = (base = 'FilesTable') => {
		const { className } = this.props;
		let classNames = [];
		if (base) classNames.unshift(base);
		if (className) classNames.unshift(className);

		return classNames;
	};

	onItemToggle = (selections) => {
		if (this.props.onCheckboxClick) this.props.onCheckboxClick(selections);
	};

    render() {
        const { files } = this.props;
        
        // Handle case where files might be undefined
        const visibleFiles = files || [];
        
        let rowProps = ({ fileId, staged }) => {
            return { id: fileId, className: classNames({ staged }) };
        };
        return (
            <div className="FilesTable">
                {super.render()}

                <DynamicTable
                    onItemToggle={this.onItemToggle}
                    primaryKey="fileId"
                    sorter="name"
                    sortDir={1}
                    index={2}
                    className={this.generateClassNames().join(' ')}
                    dataset={visibleFiles}
                    fields={this.createTableFields()}
                    rowProps={rowProps}
                    selections={this.props.selectedFileIds}
                    {...this.props} />

            </div>
        );
    }
}


FilesTable.propTypes = {
	/** files*/
	files: PropTypes.array.isRequired,
	showLabVantageEditedInFilesTable: PropTypes.bool,
	labVantageEditedPortalFileIds: PropTypes.instanceOf(Set),
	onOpenLabVantageUploadModal: PropTypes.func,
};

FilesTable.defaultProps = {
	files: [],
	showLabVantageEditedInFilesTable: false,
	labVantageEditedPortalFileIds: undefined,
	onOpenLabVantageUploadModal: undefined,
};

const mapStateToProps = state => {
	return {
		hasErrored: state.files.filesHasErrored,
		isLoading: state.files.filesIsLoading,
		isSelectedLoading: state.files.selectedFilesIsLoading,
		// files: state.files.data
	};
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch, ownProps) => {
	return {
		fetchSingleFile: (fileId, currProjectId, name) => dispatch(fetchSingleFile(fileId, currProjectId, name)),
		AdminFetchSingleFile: (fileId, currProjectId, name) => dispatch(AdminFetchSingleFile(fileId, currProjectId, name)),
	};
};

FilesTable.getFileTypeIcon = (fileName) => {
	let re = /(?:\.([^.]+))?$/;
	let extension = re.exec(fileName)[1];

	if (extension)
		extension = extension.toLowerCase();

	switch (extension) {
		case "xls":
		case "xlsx":
		case "csv":
			return "file-excel-o";
		case "doc":
		case "docx":
			return "file-word-o";
		case "png":
		case "gif":
			return "file-image-o";
		case "pdf":
			return "file-pdf-o";
		case "ppt":
		case "pptx":
			return "file-powerpoint-o";
		default:
			return "file-alt-o";
	}
};

export default connect(mapStateToProps, mapDispatchToProps)(withPermissions(FilesTable));